import { Loader2, MapPin, Search } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import {
  FallbackRow,
  type PlacesPickerSelection,
} from "@/components/places-picker"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

import { type BusinessSuggestion } from "./types"

// IMP-390: type-ahead via Places Autocomplete — rows show name + locality
// only; picking one resolves the website/address through the details
// endpoint under the same billed Places session. Keep the mechanics in
// sync with components/places-picker.tsx.
// IMP-457: picking a suggestion (or a free-typed URL) submits straight into
// the analyzing beat — no separate confirm card.

export function StepSearch({
  onSelect,
  onNoWebsite,
}: {
  onSelect: (b: PlacesPickerSelection) => void
  onNoWebsite: () => void
}) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<BusinessSuggestion[]>([])
  const [loading, setLoading] = useState(false)
  // The query the current `results` actually answer. `loading` can't stand in
  // for this: it's set from the effect, which runs after the render where the
  // query first reaches two characters — so for one frame results were empty
  // AND loading was false, flashing the "no match" fallback before the search
  // had even been issued.
  const [settledQuery, setSettledQuery] = useState<string | null>(null)
  const [resolving, setResolving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // One Places session per lookup: minted on the first keystroke, spent by
  // the details fetch on selection.
  const sessionTokenRef = useRef<string | null>(null)

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([])
      setLoading(false)
      return
    }
    setLoading(true)
    sessionTokenRef.current ??= crypto.randomUUID()
    const controller = new AbortController()
    const timer = setTimeout(() => {
      const params = new URLSearchParams({
        query,
        session_token: sessionTokenRef.current ?? "",
      })
      fetch(`/onboarding/search?${params.toString()}`, {
        signal: controller.signal,
        headers: { Accept: "application/json" },
      })
        .then((r) => r.json())
        .then((data: { results: BusinessSuggestion[] }) => {
          setResults(data.results ?? [])
          setSettledQuery(query.trim())
          setLoading(false)
        })
        .catch((e: unknown) => {
          if ((e as Error).name === "AbortError") return
          // A failed lookup still settles: the fallback row is the way out.
          setSettledQuery(query.trim())
          setLoading(false)
        })
    }, 200)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [query])

  // Predictions only carry name + locality; resolve the website and address
  // before submitting. Failures degrade to the prediction's name — the
  // server-side scrape re-fetches details anyway.
  function choosePrediction(r: BusinessSuggestion) {
    if (resolving) return
    setResolving(true)
    const token = sessionTokenRef.current
    sessionTokenRef.current = null
    const params = new URLSearchParams({ place_id: r.place_id })
    if (token) params.set("session_token", token)
    fetch(`/onboarding/place_details?${params.toString()}`, {
      headers: { Accept: "application/json" },
    })
      .then((res) => res.json())
      .then(
        (data: {
          result?: {
            name?: string | null
            website?: string | null
            address?: string | null
          }
        }) => {
          onSelect({
            place_id: r.place_id,
            name: data.result?.name ?? r.name,
            website: data.result?.website ?? null,
            address: data.result?.address ?? null,
          })
        },
      )
      .catch(() => {
        onSelect({
          place_id: r.place_id,
          name: r.name,
          website: null,
          address: null,
        })
      })
  }

  function chooseFallback(selection: PlacesPickerSelection) {
    sessionTokenRef.current = null
    onSelect(selection)
  }

  const showResults = query.trim().length >= 2
  // Only offer the fallback once a search has actually come back for exactly
  // what's typed now — otherwise it appears while the request is still in
  // flight, or against a stale query mid-debounce.
  const settled = settledQuery === query.trim()

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 space-y-8 duration-500">
      <div className="space-y-2 text-center">
        <h1 className="text-xl font-semibold">Where do I work?</h1>
        <p className="text-muted-foreground text-sm">
          I&rsquo;ll look you up and learn the basics so you don&rsquo;t have to
          type them.
        </p>
      </div>

      <div className="mx-auto max-w-md space-y-2">
        <div className="relative">
          <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            ref={inputRef}
            placeholder="Business name or website…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
            className="pl-9 text-sm placeholder:text-sm"
          />
          {(loading || resolving) && (
            <Loader2 className="text-muted-foreground absolute top-1/2 right-3 size-4 -translate-y-1/2 animate-spin" />
          )}
        </div>

        {showResults &&
          (settled && results.length === 0 ? (
            <div className="border-border bg-background overflow-hidden rounded-lg border">
              <FallbackRow query={query} onChoose={chooseFallback} />
            </div>
          ) : (
            results.map((r) => (
              <button
                key={r.place_id}
                type="button"
                disabled={resolving}
                onClick={() => choosePrediction(r)}
                className={cn(
                  "border-border bg-background flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:border-blue-300",
                  resolving && "opacity-60",
                )}
              >
                <MapPin className="text-muted-foreground size-4 shrink-0" />
                <span className="flex-1">
                  <span className="block text-sm font-medium">{r.name}</span>
                  {r.locality && (
                    <span className="text-muted-foreground block text-xs">
                      {r.locality}
                    </span>
                  )}
                </span>
              </button>
            ))
          ))}
      </div>

      <div className="text-center">
        <Button
          type="button"
          variant="ghost"
          className="text-muted-foreground hover:text-foreground text-sm"
          onClick={onNoWebsite}
        >
          I don&rsquo;t have a website
        </Button>
      </div>
    </div>
  )
}
